/**
 * Shades of India Control — storefront modules.
 * Replaces paid apps with owned modules configured from the Shades dashboard (shop.metafields.soi.config):
 *   cart     — cart drawer with free-shipping progress and upsells
 *   search   — predictive search with autocomplete, NLP query understanding and voice input
 *   filters  — AJAX collection/search filtering, sorting and load-more (Shopify section rendering)
 *   wishlist — favourites for guests (browser) and customers (synced through the app proxy)
 *   sizeChart— size chart modal with inches/cm toggle, assigned per product / type / collection / tag
 *   complementary — "Style it with" product from Shopify recommendations
 *   backInStock — out-of-stock notification sign-up
 * Theme contract (Shades sections): data-soi-* hooks and soi:* events documented in assets/soi-theme.js.
 */
(() => {
  'use strict';
  if (window.SOI && window.SOI.loaded) return;

  const ctx = window.SOI_CONTEXT || {};
  let stored = null;
  try { stored = JSON.parse(document.getElementById('soi-config')?.textContent || 'null'); } catch (e) { stored = null; }

  const DEFAULTS = {
    search: {
      enabled: true, placeholder: 'Search silk kurtas, cushions, kaftans...', limit: 8, voice: true,
      popular: ['Silk Kaftans', 'Summer Collection', 'Chinar Kurta', 'Cushion Covers'],
      synonyms: [
        { from: 'kurti', to: 'kurta' }, { from: 'kurtis', to: 'kurtas' }, { from: 'bedsheet', to: 'bedcover' },
        { from: 'bed sheet', to: 'bedcover' }, { from: 'scarf', to: 'stole' }, { from: 'jewelry', to: 'jewellery' },
        { from: 'purse', to: 'potli' }, { from: 'bag', to: 'potli' }, { from: 'shoes', to: 'jutti' }, { from: 'pyjama', to: 'pajama' },
      ],
      categories: [
        { word: 'kurta', type: 'Kurtas' }, { word: 'kaftan', type: 'Kaftans' }, { word: 'dupatta', type: 'Dupattas' },
        { word: 'dress', type: 'Dresses' }, { word: 'shirt', type: 'Shirts & Tops' }, { word: 'top', type: 'Shirts & Tops' },
        { word: 'cushion', type: 'Cushions' }, { word: 'bedcover', type: 'Bedcovers' }, { word: 'runner', type: 'Table Linens' },
        { word: 'necklace', type: 'Necklaces' }, { word: 'earring', type: 'Earrings' }, { word: 'pendant', type: 'Pendants' },
      ],
      colors: ['red', 'maroon', 'black', 'white', 'ivory', 'beige', 'pink', 'green', 'blue', 'teal', 'yellow', 'gold', 'silver', 'brown', 'grey', 'orange', 'purple'],
    },
    filters: { enabled: true, ajax: true, perPage: 24, pushDownSoldOut: true, hideSoldOut: false },
    cart: {
      enabled: true, openOnAdd: true, freeShippingThreshold: 12000,
      freeShippingText: 'You are {amount} away from free domestic shipping',
      freeShippingDone: 'You have unlocked free domestic shipping',
      upsellEnabled: true, upsellHeading: 'Complete the look', upsellLimit: 4, upsellIntent: 'complementary',
      note: true, noteLabel: 'Add a gift note', checkoutLabel: 'CHECKOUT', title: 'Your Bag',
      emptyText: 'Your bag is empty', emptyLink: '/collections/all', trustText: 'Taxes included. Shipping calculated at checkout.',
    },
    wishlist: { enabled: true, guest: true, pageTitle: 'Favourites', emptyText: 'You have not saved any favourites yet.' },
    sizeChart: { enabled: true, unitToggle: true, defaultUnit: 'in', showDiagram: true, charts: [], rules: [], defaultChartId: '' },
    recommendations: { enabled: true },
    pairWith: { enabled: true, source: 'curated_then_auto', limit: 1 },
    currency: { enabled: true, showFlags: true, mode: 'auto', currencies: ['INR', 'USD', 'GBP', 'EUR', 'AED', 'SGD', 'CAD', 'AUD'], rates: {}, autoDetect: true, note: 'Prices in {currency} are approximate. You will be charged in INR at checkout.' },
    backInStock: {
      enabled: true, heading: 'Notify me when it is back', text: 'Leave your email and we will write to you as soon as this size is available.',
      button: 'NOTIFY ME', success: 'Thank you — we will email you when it is back in stock.',
    },
    badges: { enabled: true },
    campaign: { enabled: false },
  };

  const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);
  const merge = (base, over) => {
    const out = { ...base };
    if (!isObj(over)) return out;
    Object.keys(over).forEach((k) => { out[k] = isObj(base[k]) && isObj(over[k]) ? merge(base[k], over[k]) : over[k]; });
    return out;
  };
  const cfg = merge(DEFAULTS, stored || {});
  const root = ctx.root || '/';
  const proxy = ctx.proxy || '/apps/soi';

  /* ---------------- utilities ---------------- */
  const $$ = (sel, r = document) => Array.from(r.querySelectorAll(sel));
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  const url = (path) => `${root.replace(/\/$/, '')}/${String(path).replace(/^\//, '')}`;
  const getJSON = async (u, opts) => {
    const r = await fetch(u, { headers: { Accept: 'application/json' }, ...opts });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(data.description || data.message || `Request failed (${r.status})`), { data });
    return data;
  };
  const postJSON = (u, body) => getJSON(u, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  const money = (cents) => {
    const fmt = ctx.moneyFormat || 'Rs. {{amount}}';
    const v = Number(cents) / 100;
    const d = (n, p) => n.toFixed(p).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
      if (k === 'amount_no_decimals') return d(v, 0);
      if (k === 'amount_with_comma_separator') return d(v, 2).replace('.', ',');
      if (k === 'amount_no_decimals_with_comma_separator') return d(v, 0).replace(/,/g, '.');
      return d(v, 2);
    }).replace(/[.,]00(?!\d)/, '');
  };
  const imgUrl = (src, w) => {
    if (!src) return '';
    const s = typeof src === 'string' ? src : src.src || src.url || '';
    if (!s) return '';
    const u = s.startsWith('//') ? `https:${s}` : s;
    try { const x = new URL(u, location.origin); x.searchParams.set('width', w); return x.toString(); } catch (e) { return u; }
  };
  const lock = (on) => document.body.classList.toggle('soi-lock', on);
  let toastEl;
  const toast = (msg) => {
    if (window.SOI_TOAST) { window.SOI_TOAST(msg, 'info'); return; }
    if (!toastEl) { toastEl = document.createElement('div'); toastEl.className = 'soi-app soi-toast'; toastEl.setAttribute('role', 'status'); document.body.appendChild(toastEl); }
    toastEl.textContent = msg;
    toastEl.classList.add('is-on');
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(() => toastEl.classList.remove('is-on'), 2200);
  };
  const icon = {
    close: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
    bagAdd: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><line x1="12" y1="10" x2="12" y2="16"></line><line x1="9" y1="13" x2="15" y2="13"></line></svg>',
    heart: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l8.78-8.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>',
    mic: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3"></rect><path d="M5 10a7 7 0 0 0 14 0"></path><line x1="12" y1="17" x2="12" y2="22"></line></svg>',
  };

  const SOI = window.SOI = window.SOI || {};
  Object.assign(SOI, { loaded: true, config: cfg, context: ctx, money, toast });

  /* =====================================================================
     CART DRAWER + UPSELLS
     ===================================================================== */
  const Cart = (() => {
    const c = cfg.cart;
    const enabled = c.enabled !== false && ctx.embed?.cartDrawer !== false;
    let el; let cart = null; let busy = false;

    const build = () => {
      el = document.createElement('div');
      el.className = 'soi-app soi-cart';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = `
        <div class="soi-cart__backdrop" data-soi-cart-close></div>
        <aside class="soi-cart__panel" role="dialog" aria-modal="true" aria-label="${esc(c.title)}">
          <header class="soi-cart__head">
            <h2 class="soi-cart__title">${esc(c.title)} <span class="soi-cart__count" data-count></span></h2>
            <button type="button" class="soi-cart__x" data-soi-cart-close aria-label="Close bag">${icon.close}</button>
          </header>
          <div class="soi-cart__ship" data-ship hidden><p class="soi-cart__ship-text" data-ship-text></p><div class="soi-cart__bar"><span data-ship-bar></span></div></div>
          <div class="soi-cart__scroll">
            <ul class="soi-cart__items" data-items></ul>
            <div class="soi-cart__empty" data-empty hidden>
              <p>${esc(c.emptyText)}</p>
              <a href="${esc(c.emptyLink || url('collections/all'))}" class="soi-btn soi-btn--dark">CONTINUE SHOPPING</a>
            </div>
            <section class="soi-cart__upsell" data-upsell hidden>
              <h3 class="soi-cart__upsell-title">${esc(c.upsellHeading)}</h3>
              <div class="soi-cart__upsell-track" data-upsell-track></div>
            </section>
          </div>
          <footer class="soi-cart__foot" data-foot hidden>
            ${c.note ? `<details class="soi-cart__note"><summary>${esc(c.noteLabel)}</summary><textarea data-note rows="2" maxlength="500" aria-label="${esc(c.noteLabel)}"></textarea></details>` : ''}
            <div class="soi-cart__row"><span>Subtotal</span><strong data-subtotal></strong></div>
            <div class="soi-cart__row soi-cart__row--disc" data-discount hidden><span>Savings</span><span data-discount-amount></span></div>
            <p class="soi-cart__trust">${esc(c.trustText)}</p>
            ${window.merchantInfo ? '<div class="gokwik-checkout"><button type="button" class="button" onclick="onCheckoutClick(this)"><span class="btn-text"><span>Checkout</span></span><div style="display:none" class="addloadr" id="btn-loader"><div class="cir-loader">Loading..</div></div></button></div>' : ''}
            <a href="${url('checkout')}" class="soi-btn soi-btn--dark soi-btn--block${window.merchantInfo ? ' shopifycheckoutbtn' : ''}" data-checkout>${esc(c.checkoutLabel)}</a>
          </footer>
        </aside>`;
      document.body.appendChild(el);
      el.addEventListener('click', onClick);
      el.addEventListener('change', onChange);
      const note = el.querySelector('[data-note]');
      if (note) note.addEventListener('input', debounce(() => postJSON(url('cart/update.js'), { note: note.value }).catch(() => {}), 600));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && el.classList.contains('is-open')) close(); });
    };

    const open = async () => {
      if (!el) build();
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      lock(true);
      await refresh();
      el.querySelector('.soi-cart__x')?.focus({ preventScroll: true });
    };
    const close = () => {
      el?.classList.remove('is-open');
      el?.setAttribute('aria-hidden', 'true');
      lock(false);
    };

    const refresh = async () => {
      cart = await getJSON(url('cart.js'));
      render();
      emit('soi:cart:updated', { cart, source: 'soi-core' });
      return cart;
    };

    const render = () => {
      if (!el || !cart) return;
      const items = el.querySelector('[data-items]');
      el.querySelector('[data-count]').textContent = cart.item_count ? `(${cart.item_count})` : '';
      el.querySelector('[data-empty]').hidden = cart.item_count > 0;
      el.querySelector('[data-foot]').hidden = cart.item_count === 0;
      items.innerHTML = cart.items.map((it) => `
        <li class="soi-cart__item" data-key="${esc(it.key)}">
          <a href="${esc(it.url)}" class="soi-cart__img">${it.image ? `<img src="${esc(imgUrl(it.image, 240))}" alt="${esc(it.product_title)}" width="80" height="104" loading="lazy">` : ''}</a>
          <div class="soi-cart__info">
            <a href="${esc(it.url)}" class="soi-cart__name">${esc(it.product_title)}</a>
            ${it.product_has_only_default_variant ? '' : `<p class="soi-cart__variant">${esc(it.variant_title || '')}</p>`}
            ${(it.properties && Object.keys(it.properties).filter((k) => !k.startsWith('_')).length) ? `<p class="soi-cart__variant">${Object.entries(it.properties).filter(([k]) => !k.startsWith('_')).map(([k, v]) => `${esc(k)}: ${esc(v)}`).join('<br>')}</p>` : ''}
            <div class="soi-cart__line">
              <div class="soi-qty" role="group" aria-label="Quantity">
                <button type="button" data-qty="-1" aria-label="Decrease">&minus;</button>
                <span>${it.quantity}</span>
                <button type="button" data-qty="1" aria-label="Increase">&plus;</button>
              </div>
              <span class="soi-cart__price">${it.original_line_price > it.final_line_price ? `<s>${money(it.original_line_price)}</s> ` : ''}<span class="soi-mrp">MRP</span> ${money(it.final_line_price)}</span>
            </div>
            <button type="button" class="soi-cart__remove" data-remove>Remove</button>
          </div>
        </li>`).join('');
      el.querySelector('[data-subtotal]').textContent = money(cart.total_price);
      const disc = el.querySelector('[data-discount]');
      if (disc) { disc.hidden = !cart.total_discount; el.querySelector('[data-discount-amount]').textContent = `−${money(cart.total_discount)}`; }
      const note = el.querySelector('[data-note]');
      if (note && document.activeElement !== note) note.value = cart.note || '';
      // free shipping progress (domestic INR only)
      const ship = el.querySelector('[data-ship]');
      const threshold = Number(c.freeShippingThreshold || 0) * 100;
      const domestic = !ctx.currency || ctx.currency === 'INR';
      if (threshold > 0 && domestic && cart.item_count > 0) {
        const left = Math.max(0, threshold - cart.total_price);
        ship.hidden = false;
        el.querySelector('[data-ship-text]').textContent = left > 0 ? c.freeShippingText.replace('{amount}', money(left)) : c.freeShippingDone;
        el.querySelector('[data-ship-bar]').style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
        ship.classList.toggle('is-done', left === 0);
      } else ship.hidden = true;
      renderUpsell();
    };

    let upsellFor = '';
    const renderUpsell = async () => {
      const box = el.querySelector('[data-upsell]');
      if (!c.upsellEnabled || !cart.items.length) { box.hidden = true; return; }
      const inCart = new Set(cart.items.map((i) => i.product_id));
      const seed = cart.items[0].product_id;
      const key = `${seed}:${[...inCart].join(',')}`;
      if (key === upsellFor) return;
      upsellFor = key;
      const limit = Number(c.upsellLimit || 4);
      let products = [];
      for (const intent of [c.upsellIntent || 'complementary', 'related']) {
        try {
          const data = await getJSON(url(`recommendations/products.json?product_id=${seed}&limit=${limit + inCart.size}&intent=${intent}`));
          products = (data.products || []).filter((p) => !inCart.has(p.id) && p.available);
        } catch (e) { products = []; }
        if (products.length) break;
      }
      products = products.slice(0, limit);
      box.hidden = !products.length;
      el.querySelector('[data-upsell-track]').innerHTML = products.map((p) => {
        const avail = p.variants.filter((v) => v.available);
        const single = p.variants.length === 1;
        return `
          <div class="soi-upsell">
            <a href="${esc(p.url)}" class="soi-upsell__img">${p.featured_image ? `<img src="${esc(imgUrl(p.featured_image, 300))}" alt="${esc(p.title)}" loading="lazy" width="120" height="150">` : ''}</a>
            <a href="${esc(p.url)}" class="soi-upsell__name">${esc(p.title)}</a>
            <span class="soi-upsell__price"><span class="soi-mrp">MRP</span> ${money(p.price)}</span>
            <div class="soi-upsell__buy">
              ${single ? `<input type="hidden" value="${avail[0]?.id || ''}" data-upsell-variant>` : `<select data-upsell-variant aria-label="Select size" class="noreplace original-selector"><option value="">Size</option>${p.variants.map((v) => `<option value="${v.id}"${v.available ? '' : ' disabled'}>${esc(v.title)}</option>`).join('')}</select>`}
              <button type="button" class="soi-upsell__add" data-upsell-add aria-label="Add ${esc(p.title)} to bag">${icon.bagAdd}</button>
            </div>
          </div>`;
      }).join('');
    };

    const change = async (key, quantity) => {
      if (busy) return;
      busy = true;
      el.classList.add('is-busy');
      try {
        cart = await postJSON(url('cart/change.js'), { id: key, quantity });
        render();
        emit('soi:cart:updated', { cart, source: 'soi-core' });
      } catch (e) { toast(e.message); } finally { busy = false; el.classList.remove('is-busy'); }
    };

    const add = async (items) => {
      const res = await postJSON(url('cart/add.js'), { items });
      emit('soi:cart:added', { items: res.items || [res], source: 'soi-core' });
      return res;
    };

    function onClick(e) {
      if (e.target.closest('[data-soi-cart-close]')) { close(); return; }
      const item = e.target.closest('[data-key]');
      const qtyBtn = e.target.closest('[data-qty]');
      if (item && qtyBtn) {
        const line = cart.items.find((i) => i.key === item.dataset.key);
        change(item.dataset.key, Math.max(0, line.quantity + Number(qtyBtn.dataset.qty)));
        return;
      }
      if (item && e.target.closest('[data-remove]')) { change(item.dataset.key, 0); return; }
      const addBtn = e.target.closest('[data-upsell-add]');
      if (addBtn) {
        const sel = addBtn.parentElement.querySelector('[data-upsell-variant]');
        if (!sel.value) { sel.classList.add('is-invalid'); sel.focus(); return; }
        addBtn.disabled = true;
        add([{ id: Number(sel.value), quantity: 1 }]).then(() => { upsellFor = ''; return refresh(); })
          .catch((err) => toast(err.message)).finally(() => { addBtn.disabled = false; });
      }
    }
    function onChange(e) { if (e.target.matches('[data-upsell-variant]')) e.target.classList.remove('is-invalid'); }

    if (enabled) {
      SOI.cart = { enabled: true, open, close, refresh, add };
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-soi-cart-open]');
        if (!t) return;
        e.preventDefault();
        open();
      });
      document.addEventListener('soi:cart:added', (e) => {
        if (e.detail?.source === 'soi-core' && el?.classList.contains('is-open')) return;
        if (c.openOnAdd !== false) open(); else refresh();
      });
      // Other add-to-cart forms on the site (EComposer blocks, legacy sections) also use the drawer
      document.addEventListener('submit', async (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement) || form.hasAttribute('data-soi-product-form')) return;
        const action = form.getAttribute('action') || '';
        if (!/\/cart\/add(\.js)?$/.test(action.split('?')[0])) return;
        if (e.defaultPrevented) return;
        e.preventDefault();
        try {
          const fd = new FormData(form);
          const r = await fetch(url('cart/add.js'), { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
          const data = await r.json();
          if (!r.ok) throw new Error(data.description || 'Could not add to bag');
          emit('soi:cart:added', { items: data.items || [data] });
        } catch (err) { toast(err.message); }
      });
    }
    return { enabled };
  })();

  /* =====================================================================
     SEARCH — autocomplete + NLP + voice
     ===================================================================== */
  const Search = (() => {
    const s = cfg.search;
    const overlay = document.querySelector('[data-soi-search-overlay]');
    if (s.enabled === false || ctx.embed?.search === false || !overlay) return { enabled: false };
    const input = overlay.querySelector('[data-soi-search-input]');
    const results = overlay.querySelector('[data-soi-search-results]');
    const popular = overlay.querySelector('[data-soi-search-popular]');
    const form = overlay.querySelector('[data-soi-search-form]');
    if (!input || !results) return { enabled: false };
    if (s.placeholder) input.placeholder = s.placeholder;
    if (popular && s.popular?.length) {
      const cloud = popular.querySelector('.tag-cloud');
      if (cloud) cloud.innerHTML = s.popular.map((t) => `<a class="search-tag" href="${url('search')}?q=${encodeURIComponent(t)}" data-soi-search-term="${esc(t)}">${esc(t)}</a>`).join('');
    }

    const STOP = new Set(['show', 'me', 'i', 'im', 'want', 'need', 'looking', 'look', 'find', 'search', 'for', 'a', 'an', 'the', 'some', 'please', 'with', 'of', 'and', 'in', 'buy', 'shop', 'get', 'any', 'something', 'that', 'is', 'are', 'my', 'women', 'womens', "women's", 'ladies', 'online']);
    const num = (t) => {
      const m = String(t).toLowerCase().replace(/[,₹]|rs\.?|inr/g, '').trim().match(/^(\d+(?:\.\d+)?)(k|thousand|l|lakh)?$/);
      if (!m) return null;
      const mult = m[2] ? ({ k: 1000, thousand: 1000, l: 100000, lakh: 100000 }[m[2]]) : 1;
      return Math.round(parseFloat(m[1]) * mult);
    };
    const NUM = '(?:₹|rs\\.?|inr)?\\s*(\\d[\\d,]*(?:\\.\\d+)?\\s*(?:k|thousand|l|lakh)?)';

    const parse = (raw) => {
      let q = ` ${String(raw).toLowerCase().replace(/[’']/g, "'").replace(/\s+/g, ' ')} `;
      const out = { min: null, max: null, sort: null, onSale: false, type: null, colors: [], chips: [] };
      let m;
      if ((m = q.match(new RegExp(`\\b(?:between|from)\\s+${NUM}\\s*(?:and|to|-)\\s*${NUM}`, 'i'))) || (m = q.match(new RegExp(`${NUM}\\s*(?:-|to)\\s*${NUM}`, 'i')))) {
        out.min = num(m[1]); out.max = num(m[2]); q = q.replace(m[0], ' ');
      }
      if ((m = q.match(new RegExp(`\\b(?:under|below|less than|upto|up to|within|max(?:imum)?|cheaper than|<)\\s*${NUM}`, 'i')))) { out.max = num(m[1]); q = q.replace(m[0], ' '); }
      if ((m = q.match(new RegExp(`\\b(?:over|above|more than|min(?:imum)?|starting(?: at| from)?|>)\\s*${NUM}`, 'i')))) { out.min = num(m[1]); q = q.replace(m[0], ' '); }
      if (/\b(new|newest|latest|just in|new arrivals?)\b/.test(q)) { out.sort = 'created-descending'; q = q.replace(/\b(newest|latest|just in|new arrivals?)\b/g, ' '); }
      if (/\b(cheap|cheapest|affordable|budget|lowest price)\b/.test(q)) { out.sort = 'price-ascending'; q = q.replace(/\b(cheap|cheapest|affordable|budget|lowest price)\b/g, ' '); }
      if (/\b(luxury|premium|expensive|high end)\b/.test(q)) { out.sort = 'price-descending'; q = q.replace(/\b(expensive|high end)\b/g, ' '); }
      if (/\b(sale|on sale|discount(?:ed)?|offers?|deals?)\b/.test(q)) { out.onSale = true; q = q.replace(/\b(on sale|sale|discount(?:ed)?|offers?|deals?)\b/g, ' '); }
      (s.synonyms || []).forEach(({ from, to }) => { if (from) q = q.replace(new RegExp(`\\b${from.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'), to.toLowerCase()); });
      (s.colors || []).forEach((col) => { if (new RegExp(`\\b${col}\\b`).test(q)) out.colors.push(col); });
      const words = q.split(' ').map((w) => w.trim()).filter((w) => w && !STOP.has(w));
      for (const { word, type } of (s.categories || [])) {
        if (words.some((w) => w === word || w === `${word}s` || w === `${word}es`)) { out.type = type; break; }
      }
      out.text = words.join(' ');
      if (out.type) out.chips.push(out.type);
      if (out.colors.length) out.chips.push(out.colors.join(', '));
      if (out.min != null && out.max != null) out.chips.push(`${money(out.min * 100)} – ${money(out.max * 100)}`);
      else if (out.max != null) out.chips.push(`under ${money(out.max * 100)}`);
      else if (out.min != null) out.chips.push(`over ${money(out.min * 100)}`);
      if (out.onSale) out.chips.push('on sale');
      if (out.sort === 'created-descending') out.chips.push('newest first');
      if (out.sort === 'price-ascending') out.chips.push('lowest price first');
      if (out.sort === 'price-descending') out.chips.push('premium first');
      return out;
    };

    const viewAllUrl = (p) => {
      const u = new URL(url('search'), location.origin);
      u.searchParams.set('q', p.text || '*');
      u.searchParams.set('type', 'product');
      u.searchParams.set('options[prefix]', 'last');
      if (p.min != null) u.searchParams.set('filter.v.price.gte', p.min);
      if (p.max != null) u.searchParams.set('filter.v.price.lte', p.max);
      if (p.type) u.searchParams.set('filter.p.product_type', p.type);
      if (p.sort) u.searchParams.set('sort_by', p.sort);
      return u.pathname + u.search;
    };

    let seq = 0;
    let last = { q: '', count: 0, parsed: null };
    const run = async () => {
      const raw = input.value.trim();
      const id = ++seq;
      if (raw.length < 2) { results.innerHTML = ''; if (popular) popular.hidden = false; return; }
      const p = parse(raw);
      const term = p.text || raw;
      let data = {};
      try {
        const params = new URLSearchParams({ q: term, 'resources[type]': 'product,collection,query', 'resources[limit]': '10', 'resources[options][unavailable_products]': 'last', 'resources[options][fields]': 'title,product_type,variants.title,vendor,tag' });
        data = (await getJSON(url(`search/suggest.json?${params}`))).resources?.results || {};
      } catch (e) { data = {}; }
      if (id !== seq) return;
      let products = (data.products || []).filter((pr) => {
        const price = parseFloat(pr.price);
        if (p.min != null && price < p.min) return false;
        if (p.max != null && price > p.max) return false;
        if (p.onSale && !(parseFloat(pr.compare_at_price_max) > price)) return false;
        if (p.type && pr.type && pr.type.toLowerCase() !== p.type.toLowerCase() && !pr.title.toLowerCase().includes(p.type.toLowerCase().replace(/s$/, ''))) return false;
        return true;
      });
      if (p.sort === 'price-ascending') products.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      if (p.sort === 'price-descending') products.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
      products = products.slice(0, Number(s.limit || 8));
      const collections = (data.collections || []).slice(0, 4);
      const queries = (data.queries || []).slice(0, 5);
      if (popular) popular.hidden = true;
      const all = viewAllUrl(p);
      results.innerHTML = `
        ${p.chips.length ? `<p class="soi-search__understood">Showing ${esc(p.text || 'products')}${p.chips.length ? ` · <span>${p.chips.map(esc).join(' · ')}</span>` : ''}</p>` : ''}
        ${queries.length ? `<div class="soi-search__queries">${queries.map((qq) => `<a class="search-tag" href="${esc(qq.url)}" data-soi-search-term="${esc(qq.text)}">${qq.styled_text || esc(qq.text)}</a>`).join('')}</div>` : ''}
        ${collections.length ? `<div class="soi-search__group"><p class="search-label">Collections</p><div class="soi-search__cols">${collections.map((cc) => `<a href="${esc(cc.url)}" class="soi-search__col">${esc(cc.title)}</a>`).join('')}</div></div>` : ''}
        ${products.length ? `<div class="soi-search__group"><p class="search-label">Products</p><div class="soi-search__products">${products.map((pr) => `
          <a href="${esc(pr.url)}" class="soi-search__product">
            <span class="soi-search__thumb">${pr.image ? `<img src="${esc(imgUrl(pr.image, 240))}" alt="${esc(pr.title)}" loading="lazy" width="120" height="150">` : ''}</span>
            <span class="soi-search__name">${esc(pr.title)}</span>
            <span class="soi-search__price">${parseFloat(pr.compare_at_price_max) > parseFloat(pr.price) ? `<s>${money(Math.round(parseFloat(pr.compare_at_price_max) * 100))}</s> ` : ''}<span class="soi-mrp">MRP</span> ${money(Math.round(parseFloat(pr.price) * 100))}</span>
          </a>`).join('')}</div></div>
          <a href="${esc(all)}" class="soi-btn soi-btn--dark soi-search__all">VIEW ALL RESULTS</a>`
          : `<div class="soi-search__none"><p>No matches for “${esc(raw)}”.</p>${(s.popular || []).length ? `<p class="search-label">Try</p><div class="tag-cloud">${s.popular.map((t) => `<a class="search-tag" href="${url('search')}?q=${encodeURIComponent(t)}" data-soi-search-term="${esc(t)}">${esc(t)}</a>`).join('')}</div>` : ''}</div>`}`;
      last = { q: raw, count: products.length, parsed: p };
      logSearch();
    };
    const logSearch = debounce(() => {
      if (!last.q) return;
      const body = JSON.stringify({ q: last.q, results: last.count, parsed: last.parsed ? { text: last.parsed.text, min: last.parsed.min, max: last.parsed.max, type: last.parsed.type, sort: last.parsed.sort, onSale: last.parsed.onSale } : null });
      try { navigator.sendBeacon(`${proxy}/search-log`, new Blob([body], { type: 'application/json' })); } catch (e) { /* analytics only */ }
    }, 1500);

    input.addEventListener('input', debounce(run, 180));
    overlay.addEventListener('click', (e) => {
      const tag = e.target.closest('[data-soi-search-term]');
      if (!tag) return;
      e.preventDefault();
      input.value = tag.dataset.soiSearchTerm;
      input.focus();
      run();
    });
    form?.addEventListener('submit', (e) => {
      const raw = input.value.trim();
      if (!raw) { e.preventDefault(); return; }
      e.preventDefault();
      last = { q: raw, count: last.count, parsed: parse(raw) };
      logSearch();
      location.href = viewAllUrl(last.parsed);
    });

    // Voice search
    const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (s.voice !== false && Speech && form) {
      const mic = document.createElement('button');
      mic.type = 'button';
      mic.className = 'soi-search__mic';
      mic.setAttribute('aria-label', 'Search by voice');
      mic.innerHTML = icon.mic;
      form.insertBefore(mic, form.querySelector('[data-soi-search-close]'));
      mic.addEventListener('click', () => {
        const rec = new Speech();
        rec.lang = ctx.locale === 'hi' ? 'hi-IN' : 'en-IN';
        rec.interimResults = false;
        mic.classList.add('is-listening');
        rec.onresult = (ev) => { input.value = ev.results[0][0].transcript; run(); };
        rec.onend = () => mic.classList.remove('is-listening');
        rec.onerror = () => mic.classList.remove('is-listening');
        rec.start();
      });
    }
    SOI.search = { enabled: true, parse };
    return { enabled: true };
  })();

  /* =====================================================================
     AJAX FILTERS
     ===================================================================== */
  const Filters = (() => {
    const f = cfg.filters;
    const rootEl = document.querySelector('[data-soi-filters-root]');
    if (!rootEl || f.enabled === false || f.ajax === false) return { enabled: false };
    SOI.filters = { enabled: true };
    const sectionId = rootEl.dataset.sectionId;
    const form = () => rootEl.querySelector('[data-soi-filter-form]');
    const withSection = (u) => { const x = new URL(u, location.origin); x.searchParams.set('section_id', sectionId); return x.toString(); };
    const clean = (u) => { const x = new URL(u, location.origin); x.searchParams.delete('section_id'); x.searchParams.delete('page'); return x.pathname + x.search; };

    const formUrl = () => {
      const fm = form();
      const x = new URL(fm.getAttribute('action'), location.origin);
      new FormData(fm).forEach((v, k) => { if (String(v).trim() !== '') x.searchParams.append(k, v); });
      return x.pathname + x.search;
    };

    let ctrl;
    const navigate = async (target, push = true) => {
      const grid = rootEl.querySelector('[data-soi-grid]');
      grid?.classList.add('is-loading');
      ctrl?.abort();
      ctrl = new AbortController();
      try {
        const html = await (await fetch(withSection(target), { signal: ctrl.signal })).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const drawerOpen = rootEl.querySelector('[data-soi-filter-drawer]')?.classList.contains('is-open');
        const openGroups = $$('[data-soi-fgroup][open] .soi-fgroup__label', rootEl).map((s) => s.textContent.trim().split(' (')[0]);
        $$('[data-soi-filter-target]', doc).forEach((fresh) => {
          const cur = rootEl.querySelector(`[data-soi-filter-target="${fresh.dataset.soiFilterTarget}"]`);
          if (cur) cur.replaceWith(fresh);
        });
        const drawer = rootEl.querySelector('[data-soi-filter-drawer]');
        if (drawerOpen && drawer) {
          drawer.classList.add('is-open');
          $$('[data-soi-fgroup]', drawer).forEach((d) => { if (openGroups.includes(d.querySelector('.soi-fgroup__label').textContent.trim().split(' (')[0])) d.open = true; });
        }
        const sortSel = rootEl.querySelector('[data-soi-sort]');
        const freshSort = doc.querySelector('[data-soi-sort]');
        if (sortSel && freshSort) sortSel.value = freshSort.value;
        // Tag facets live in the path, so the form's action changes with every filter.
        const freshForm = doc.querySelector('[data-soi-filter-form]');
        if (freshForm) form()?.setAttribute('action', freshForm.getAttribute('action'));
        if (push) history.pushState({ soiFilters: true }, '', clean(target));
        Wishlist.mark();
        emit('soi:content:loaded', { root: rootEl });
        const top = rootEl.getBoundingClientRect().top + window.scrollY - 70;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      } catch (e) {
        if (e.name !== 'AbortError') location.href = target;
      } finally {
        rootEl.querySelector('[data-soi-grid]')?.classList.remove('is-loading');
      }
    };

    rootEl.addEventListener('change', (e) => {
      if (e.target.matches('[data-soi-category-select]')) return; // handled via soi:filter:navigate
      if (e.target.matches('[data-soi-price]')) return;
      if (e.target.closest('[data-soi-filter-form]')) navigate(formUrl());
    });
    rootEl.addEventListener('input', debounce((e) => { if (e.target.matches('[data-soi-price]')) navigate(formUrl()); }, 700));
    rootEl.addEventListener('submit', (e) => {
      if (!e.target.matches('[data-soi-filter-form]')) return;
      e.preventDefault();
      navigate(formUrl());
      rootEl.soiFilterDrawer?.(false);
    });
    rootEl.addEventListener('soi:filter:navigate', (e) => { e.preventDefault(); navigate(e.detail.url); });
    rootEl.addEventListener('click', async (e) => {
      const link = e.target.closest('[data-soi-filter-link]');
      if (link) { e.preventDefault(); navigate(link.getAttribute('href')); return; }
      const more = e.target.closest('[data-soi-load-more]');
      if (!more) return;
      e.preventDefault();
      more.classList.add('is-loading');
      more.textContent = 'Loading…';
      try {
        const html = await (await fetch(withSection(more.href))).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = rootEl.querySelector('[data-soi-grid]');
        $$('[data-soi-grid] > *', doc).forEach((n) => { if (!n.classList.contains('soi-empty')) grid.appendChild(n); });
        const pag = doc.querySelector('[data-soi-filter-target="pagination"]');
        rootEl.querySelector('[data-soi-filter-target="pagination"]')?.replaceWith(pag);
        Wishlist.mark();
        emit('soi:content:loaded', { root: grid });
      } catch (err) { location.href = more.href; }
    });
    window.addEventListener('popstate', (e) => { if (e.state?.soiFilters || /filter\.|sort_by/.test(location.search)) navigate(location.href, false); });
    return { enabled: true };
  })();

  /* =====================================================================
     FAVOURITES (WISHLIST)
     ===================================================================== */
  const Wishlist = (() => {
    const w = cfg.wishlist;
    const KEY = 'soi:wishlist:v1';
    const enabled = w.enabled !== false;
    const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; } };
    const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* private mode */ } };
    let list = read();
    const has = (id) => list.some((i) => String(i.id) === String(id));

    const mark = () => {
      if (!enabled) return;
      $$('[data-soi-wishlist]').forEach((b) => {
        const on = has(b.dataset.productId);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', String(on));
      });
      $$('[data-soi-wishlist-count]').forEach((c) => { c.textContent = String(list.length); c.hidden = list.length === 0; });
    };
    const sync = (action, item) => {
      if (!ctx.customer) return Promise.resolve();
      return postJSON(`${proxy}/wishlist`, { action, productId: String(item.id), handle: item.handle }).catch(() => {});
    };
    const toggle = (id, handle) => {
      const on = has(id);
      if (on) list = list.filter((i) => String(i.id) !== String(id));
      else list.unshift({ id: String(id), handle, t: Date.now() });
      write(list);
      mark();
      sync(on ? 'remove' : 'add', { id, handle });
      toast(on ? 'Removed from favourites' : 'Saved to favourites');
      emit('soi:wishlist:change', { list });
      return !on;
    };

    const loadServer = async () => {
      if (!ctx.customer) return;
      try {
        const data = await getJSON(`${proxy}/wishlist?format=json`);
        const server = data.items || [];
        const ids = new Set(server.map((i) => String(i.productId)));
        const localOnly = list.filter((i) => !ids.has(String(i.id)));
        list = [...server.map((i) => ({ id: String(i.productId), handle: i.handle, t: Date.parse(i.createdAt) || 0 })), ...localOnly];
        write(list);
        if (localOnly.length) postJSON(`${proxy}/wishlist`, { action: 'merge', items: localOnly.map((i) => ({ productId: i.id, handle: i.handle })) }).catch(() => {});
        mark();
        renderPage();
      } catch (e) { /* offline */ }
    };

    const renderPage = async () => {
      const page = document.querySelector('[data-soi-wishlist-page]');
      if (!page) return;
      const grid = page.querySelector('[data-soi-wishlist-grid]');
      const empty = page.querySelector('[data-soi-wishlist-empty]');
      if (!list.length) { grid.innerHTML = ''; empty.hidden = false; return; }
      empty.hidden = true;
      const products = (await Promise.all(list.map((i) => getJSON(url(`products/${i.handle}.js`)).catch(() => null)))).filter(Boolean);
      grid.innerHTML = products.map((p) => `
        <div class="collection-product-card soi-card" data-soi-card>
          <a href="${esc(p.url)}" class="soi-card__link" aria-label="${esc(p.title)}"></a>
          <div class="collection-product-img-wrap">
            ${p.featured_image ? `<img src="${esc(imgUrl(p.featured_image, 700))}" alt="${esc(p.title)}" class="collection-product-img" loading="lazy">` : ''}
            ${p.available ? '' : '<span class="collection-card-tag soi-soldout-tag">Sold out</span>'}
            <button type="button" class="soi-card__wish is-active" style="display:flex" data-soi-wishlist data-product-id="${p.id}" data-product-handle="${esc(p.handle)}" aria-label="Remove ${esc(p.title)} from favourites" aria-pressed="true">${icon.heart}</button>
          </div>
          <div class="collection-product-details">
            <h3 class="collection-product-title">${esc(p.title)}</h3>
            <div class="collection-product-price"><span class="current-price"><span class="soi-mrp">MRP</span> ${money(p.price)}</span>${p.compare_at_price > p.price ? `<span class="slashed-price">${money(p.compare_at_price)}</span>` : ''}</div>
            <div class="soi-wish-buy">
              ${p.variants.length > 1 ? `<select class="noreplace original-selector pdp-cross-sell-size-select" data-wish-variant aria-label="Select size"><option value="">Select Size</option>${p.variants.map((v) => `<option value="${v.id}"${v.available ? '' : ' disabled'}>${esc(v.title)}</option>`).join('')}</select>` : `<input type="hidden" data-wish-variant value="${p.variants[0].id}">`}
              <button type="button" class="soi-btn soi-btn--dark soi-btn--sm" data-wish-add${p.available ? '' : ' disabled'}>${p.available ? 'ADD TO BAG' : 'SOLD OUT'}</button>
            </div>
          </div>
        </div>`).join('');
    };

    if (enabled) {
      SOI.wishlist = { enabled: true, toggle, list: () => list.slice(), mark };
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-soi-wishlist]');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();
          toggle(btn.dataset.productId, btn.dataset.productHandle);
          if (btn.closest('[data-soi-wishlist-page]') && !has(btn.dataset.productId)) btn.closest('[data-soi-card]')?.remove();
          if (!list.length) renderPage();
          return;
        }
        const add = e.target.closest('[data-wish-add]');
        if (add) {
          const sel = add.parentElement.querySelector('[data-wish-variant]');
          if (!sel.value) { sel.classList.add('is-invalid'); sel.focus(); return; }
          add.disabled = true;
          postJSON(url('cart/add.js'), { items: [{ id: Number(sel.value), quantity: 1 }] })
            .then((res) => emit('soi:cart:added', { items: res.items || [res] }))
            .catch((err) => toast(err.message)).finally(() => { add.disabled = false; });
        }
      }, true);
      document.addEventListener('soi:content:loaded', mark);
    }
    return { enabled, mark, renderPage, loadServer };
  })();

  /* =====================================================================
     SIZE CHART — table with inches/cm toggle
     ===================================================================== */
  const SizeChart = (() => {
    const sc = cfg.sizeChart;
    if (sc.enabled === false) return { enabled: false };
    const UKEY = 'soi:size-unit';
    const charts = sc.charts || [];
    const resolve = () => {
      const p = ctx.product;
      if (!p) return null;
      const byId = (id) => charts.find((c) => c.id === id);
      if (p.sizeChart && byId(p.sizeChart)) return byId(p.sizeChart);
      const tags = (p.tags || []).map((t) => String(t).toLowerCase());
      const tagChart = tags.find((t) => t.startsWith('size-chart:'));
      if (tagChart && byId(tagChart.split(':')[1].trim())) return byId(tagChart.split(':')[1].trim());
      for (const r of (sc.rules || [])) {
        const v = String(r.value || '').toLowerCase();
        const hit = (r.field === 'type' && String(p.type || '').toLowerCase() === v)
          || (r.field === 'vendor' && String(p.vendor || '').toLowerCase() === v)
          || (r.field === 'tag' && tags.includes(v))
          || (r.field === 'collection' && (p.collections || []).includes(v));
        if (hit && byId(r.chart)) return byId(r.chart);
      }
      return sc.defaultChartId ? byId(sc.defaultChartId) : null;
    };
    const fmt = (n) => (Math.round(n * 10) / 10).toString();
    const convert = (val, from, to) => {
      if (from === to) return val;
      return String(val).replace(/\d+(?:\.\d+)?/g, (m) => fmt(from === 'in' ? parseFloat(m) * 2.54 : parseFloat(m) / 2.54));
    };
    const render = (body, chart) => {
      const base = chart.unit || 'in';
      let unit = localStorage.getItem(UKEY) || sc.defaultUnit || base;
      const draw = () => {
        const cols = chart.columns || [];
        body.innerHTML = `
          <div class="soi-app soi-size">
            <h3 class="soi-size__title">${esc(chart.title || chart.name || 'Size Chart')}</h3>
            ${(chart.image || (sc.showDiagram !== false && chart.showDiagram !== false && ctx.howToMeasure)) ? `<p class="soi-size__how">HOW TO MEASURE?</p><img class="soi-size__diagram" src="${esc(chart.image || ctx.howToMeasure)}" alt="How to measure" loading="lazy">` : ''}
            ${sc.unitToggle !== false ? `<div class="soi-size__units" role="tablist" aria-label="Units">
              <button type="button" role="tab" data-unit="in" aria-selected="${unit === 'in'}" class="${unit === 'in' ? 'is-on' : ''}">INCHES</button>
              <button type="button" role="tab" data-unit="cm" aria-selected="${unit === 'cm'}" class="${unit === 'cm' ? 'is-on' : ''}">CM</button>
            </div>` : ''}
            <div class="soi-size__scroll"><table class="soi-size__table">
              <thead><tr><th scope="col"></th>${cols.map((c) => `<th scope="col">${esc(c)}</th>`).join('')}</tr></thead>
              <tbody>${(chart.rows || []).map((r) => `<tr><th scope="row">${esc(r.label)}</th>${(r.values || []).map((v) => `<td>${esc(convert(v, base, unit))}</td>`).join('')}</tr>`).join('')}</tbody>
            </table></div>
            ${chart.note ? `<p class="soi-size__note">${esc(chart.note)}</p>` : ''}
          </div>`;
        $$('[data-unit]', body).forEach((b) => b.addEventListener('click', () => { unit = b.dataset.unit; try { localStorage.setItem(UKEY, unit); } catch (e) { /* ignore */ } draw(); }));
      };
      draw();
    };
    document.addEventListener('soi:size-chart:open', (e) => {
      const body = e.detail?.body;
      const chart = resolve();
      if (body && chart) render(body, chart);
    });
    SOI.sizeChart = { enabled: true, resolve };
    return { enabled: true };
  })();

  /* =====================================================================
     "STYLE IT WITH" — complementary product for the PDP slot
     ===================================================================== */
  const Complementary = (async () => {
    const slot = document.querySelector('[data-soi-complementary]');
    if (!slot || cfg.pairWith?.enabled === false || cfg.pairWith?.source === 'curated') return;
    const limit = Math.max(1, Math.min(12, parseInt(slot.dataset.limit, 10) || 6));
    const own = String(slot.dataset.productId);
    const found = [];
    for (const intent of ['complementary', 'related']) {
      if (found.length >= limit) break;
      try {
        const data = await getJSON(url(`recommendations/products.json?product_id=${own}&limit=${Math.min(limit + 2, 12)}&intent=${intent}`));
        (data.products || []).forEach((p) => {
          if (p.available && String(p.id) !== own && !found.some((f) => f.id === p.id) && found.length < limit) found.push(p);
        });
      } catch (e) { /* try the next intent */ }
    }
    if (!found.length) return;
    // Same card as the curated list (the product "card" view, which includes the quick add-to-bag button)
    const cards = (await Promise.all(found.map(async (p) => {
      try {
        const r = await fetch(url(`products/${p.handle}?view=card`), { credentials: 'same-origin' });
        return r.ok ? (await r.text()).trim() : '';
      } catch (e) { return ''; }
    }))).filter(Boolean);
    if (!cards.length) return;
    const arrows = cards.length > 1 ? `
        <div class="soi-pair-list__nav" data-soi-pair-nav hidden>
          <button type="button" class="soi-pair-list__arrow" data-soi-pair-prev aria-label="Previous products" disabled><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg></button>
          <button type="button" class="soi-pair-list__arrow" data-soi-pair-next aria-label="Next products"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></button>
        </div>` : '';
    slot.outerHTML = `
      <div class="soi-pair-list" data-soi-pair>
        <div class="soi-pair-list__head">
          <h2 class="soi-pair-list__title"><span class="soi-only-mobile">${esc(slot.dataset.heading || 'Style it with')}</span><span class="soi-only-desktop">${esc(slot.dataset.desktopHeading || 'Pair it with')}</span></h2>${arrows}
        </div>
        <div class="soi-pair-list__track" data-soi-pair-track>${cards.map((c) => `<div class="soi-pair-card">${c}</div>`).join('')}</div>
      </div>`;
    if (window.__soiPairRefresh) window.__soiPairRefresh();
  })();

  /* =====================================================================
     BACK IN STOCK
     ===================================================================== */
  const BackInStock = (() => {
    const b = cfg.backInStock;
    if (b.enabled === false) return;
    const productJson = (() => { try { return JSON.parse(document.querySelector('[data-soi-product-json]')?.textContent || 'null'); } catch (e) { return null; } })();
    const render = (slot) => {
      if (slot.dataset.soiRendered) return;
      slot.dataset.soiRendered = '1';
      slot.innerHTML = `
        <div class="soi-app soi-bis">
          <p class="soi-bis__title">${esc(b.heading)}</p>
          <p class="soi-bis__text">${esc(b.text)}</p>
          <div class="soi-bis__form" data-bis-form>
            <input type="email" name="soi_bis_email" autocomplete="email" placeholder="Your email address" aria-label="Email" value="${esc(ctx.customer?.email || '')}">
            <button type="button" class="soi-btn soi-btn--dark" data-bis-submit>${esc(b.button)}</button>
          </div>
          <p class="soi-bis__msg" role="status" aria-live="polite"></p>
        </div>`;
      const form = slot.querySelector('[data-bis-form]');
      const input = form.querySelector('input');
      const btn = form.querySelector('[data-bis-submit]');
      const msg = slot.querySelector('.soi-bis__msg');
      // The slot sits inside the product form, so it uses a button + Enter key instead of a nested <form>.
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); btn.click(); } });
      btn.addEventListener('click', async () => {
        const email = input.value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { msg.textContent = 'Please enter a valid email address.'; return; }
        const variant = productJson?.variants?.find((v) => String(v.id) === String(slot.dataset.variantId));
        btn.disabled = true;
        try {
          await postJSON(`${proxy}/bis`, {
            email, productId: String(slot.dataset.productId), variantId: String(slot.dataset.variantId),
            productTitle: slot.dataset.productTitle || productJson?.title || '', variantTitle: variant?.title || '',
            handle: productJson?.handle || ctx.product?.handle || '', locale: ctx.locale,
          });
          msg.textContent = b.success;
          form.hidden = true;
        } catch (err) {
          msg.textContent = err.message || 'Something went wrong. Please try again.';
        } finally { btn.disabled = false; }
      });
    };
    $$('[data-soi-bis]').forEach(render);
    document.addEventListener('soi:variant:change', () => {
      $$('[data-soi-bis]').forEach((slot) => {
        const form = slot.querySelector('[data-bis-form]');
        const msg = slot.querySelector('.soi-bis__msg');
        if (form) form.hidden = false;
        if (msg) msg.textContent = '';
      });
    });
  })();

  /* =====================================================================
     DISPLAY CURRENCY (single-market stores: checkout stays in the shop currency)
     ===================================================================== */
  const Currency = (() => {
    const cc = cfg.currency;
    const pickers = $$('[data-soi-currency-root], [data-soi-display-currency]');
    const base = (ctx.currency || 'INR').toUpperCase();
    if (cc.enabled === false || !pickers.length) return { enabled: false };
    const wanted = (Array.isArray(cc.currencies) && cc.currencies.length ? cc.currencies : ['USD', 'GBP', 'EUR', 'AED', 'SGD', 'CAD', 'AUD'])
      .map((c) => String(c).trim().toUpperCase()).filter((c) => c && c !== base);
    // Rates start from the dashboard-configured values (if any) and are replaced by live rates fetched in the browser.
    const rates = { [base]: 1, ...(cc.rates || {}) };
    let ratesAt = cc.ratesUpdatedAt || null;
    const KEY = 'soi:currency';
    const RATES_KEY = `soi:rates:${base}`;
    const RATES_TTL = 60 * 60 * 1000; // re-fetch at most hourly (the public feeds themselves update about once a day)
    let fetchedAt = 0;
    const SOURCES = [
      {
        url: (b) => `https://open.er-api.com/v6/latest/${b}`,
        pick: (j) => (j && j.result === 'success' ? j.rates : null),
        at: (j) => j.time_last_update_utc || null,
      },
      {
        url: (b) => `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${b.toLowerCase()}.json`,
        pick: (j) => {
          const m = j && j[base.toLowerCase()];
          if (!m) return null;
          const o = {};
          Object.keys(m).forEach((k) => { o[k.toUpperCase()] = m[k]; });
          return o;
        },
        at: (j) => j.date || null,
      },
    ];
    try {
      const c = JSON.parse(localStorage.getItem(RATES_KEY) || 'null');
      if (c && c.rates && typeof c.rates === 'object') { Object.assign(rates, c.rates); ratesAt = c.at || ratesAt; fetchedAt = Number(c.t) || 0; }
    } catch (e) { /* storage unavailable */ }
    const refreshRates = async () => {
      for (const s of SOURCES) {
        try {
          const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
          const timer = setTimeout(() => ctrl && ctrl.abort(), 7000);
          const res = await fetch(s.url(base), { cache: 'no-cache', signal: ctrl ? ctrl.signal : undefined });
          clearTimeout(timer);
          if (!res.ok) continue;
          const j = await res.json();
          const all = s.pick(j);
          if (!all) continue;
          const next = {};
          wanted.forEach((c) => { const r = Number(all[c]); if (r > 0 && isFinite(r)) next[c] = r; });
          if (!Object.keys(next).length) continue;
          Object.assign(rates, next);
          ratesAt = s.at(j) || new Date().toISOString();
          fetchedAt = Date.now();
          try { localStorage.setItem(RATES_KEY, JSON.stringify({ t: fetchedAt, at: ratesAt, rates: next })); } catch (e) { /* private mode */ }
          return true;
        } catch (e) { /* network/CORS/timeout — try the next source */ }
      }
      return false;
    };
    const TZ = { 'America/': 'USD', 'Europe/London': 'GBP', 'Asia/Dubai': 'AED', 'Asia/Singapore': 'SGD', 'Australia/': 'AUD', 'America/Toronto': 'CAD', 'America/Vancouver': 'CAD', 'Europe/': 'EUR' };
    const detect = () => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        const hit = Object.keys(TZ).sort((a, b) => b.length - a.length).find((k) => tz.startsWith(k));
        return hit ? TZ[hit] : base;
      } catch (e) { return base; }
    };
    let saved = null;
    try { saved = localStorage.getItem(KEY); } catch (e) { saved = null; }
    let desired = (saved || (cc.autoDetect !== false ? detect() : base) || base).toUpperCase();
    // Until a live rate is known for the chosen currency, keep showing the shop currency (never a made-up number).
    const resolve = () => (desired === base || rates[desired] ? desired : base);
    let current = resolve();
    const SEL = '.current-price, .slashed-price, .pdp-current-price, .pdp-compare-price, .pdp-cross-sell-price, .soi-cart__price, [data-subtotal], [data-discount-amount], .soi-upsell__price, .soi-search__price, [data-soi-money]';
    const parse = (txt) => { const m = String(txt).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/g); return m ? m.map(Number) : null; };
    const fmt = (amount, code) => {
      try { return new Intl.NumberFormat(code === 'INR' ? 'en-IN' : 'en', { style: 'currency', currency: code, maximumFractionDigits: code === 'INR' ? 0 : 2, minimumFractionDigits: code === 'INR' ? 0 : 2 }).format(amount); } catch (e) { return `${code} ${amount.toFixed(2)}`; }
    };
    const convertEl = (el) => {
      if (el.closest('.soi-currency')) return;
      // Keep the original (shop currency) text so switching back is exact and repeated passes are idempotent.
      if (el.dataset.soiBase === undefined || el.dataset.soiShown !== el.innerHTML) { el.dataset.soiBase = el.innerHTML; }
      const baseHtml = el.dataset.soiBase;
      if (current === base) { if (el.innerHTML !== baseHtml) el.innerHTML = baseHtml; el.dataset.soiShown = el.innerHTML; return; }
      const rate = rates[current];
      let html = baseHtml;
      // Replace each money amount inside the element (keeps prefixes such as "MRP" or "−").
      html = html.replace(/(?:Rs\.?|₹|INR)\s*([\d,]+(?:\.\d+)?)/g, (m, n) => fmt(parseFloat(n.replace(/,/g, '')) * rate, current));
      el.innerHTML = html;
      el.dataset.soiShown = el.innerHTML;
    };
    let busy = false;
    const apply = (root = document) => {
      if (busy) return;
      busy = true;
      $$(SEL, root).forEach(convertEl);
      if (root.matches && root.matches(SEL)) convertEl(root);
      $$('[data-soi-currency-code]').forEach((c) => { c.textContent = current; });
      $$('[data-soi-currency-option]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.soiCurrencyOption === current)));
      let note = document.querySelector('.soi-cart__currency-note');
      const foot = document.querySelector('.soi-cart__foot');
      if (foot && !note) { note = document.createElement('p'); note.className = 'soi-cart__currency-note'; foot.insertBefore(note, foot.querySelector('[data-checkout]')); }
      if (note) { note.hidden = current === base; note.textContent = (cc.note || '').replace('{currency}', current); }
      busy = false;
    };
    const closeAll = () => $$('[data-soi-currency-root]').forEach((r) => { r.classList.remove('is-open'); r.querySelector('[data-soi-currency-toggle]')?.setAttribute('aria-expanded', 'false'); });
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-soi-currency-toggle]');
      const option = e.target.closest('[data-soi-currency-option]');
      if (toggle) {
        e.preventDefault();
        const root = toggle.closest('[data-soi-currency-root]');
        const open = !root.classList.contains('is-open');
        closeAll();
        root.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        return;
      }
      if (option) {
        e.preventDefault();
        desired = String(option.dataset.soiCurrencyOption || base).toUpperCase();
        try { localStorage.setItem(KEY, desired); } catch (err) { /* private mode */ }
        closeAll();
        const choose = () => {
          current = resolve();
          apply();
          if (current === base && desired !== base) { toast('Live exchange rates are unavailable right now — showing prices in ' + base); return; }
          toast(current === base ? `Showing prices in ${base}` : `Showing approximate prices in ${current} (1 ${current} ≈ ${base} ${(1 / rates[current]).toFixed(2)})`);
        };
        if (desired !== base && !rates[desired]) refreshRates().then(choose); else choose();
        return;
      }
      if (!e.target.closest('[data-soi-currency-root]')) closeAll();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });
    const mo = new MutationObserver((muts) => {
      if (busy || current === base) return;
      const roots = new Set();
      muts.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1) roots.add(n); }));
      muts.forEach((m) => { if (m.type === 'characterData' || m.type === 'childList') { const el = m.target.nodeType === 1 ? m.target : m.target.parentElement; const hit = el && el.closest && el.closest(SEL); if (hit && hit.innerHTML !== hit.dataset.soiShown) { delete hit.dataset.soiBase; roots.add(hit); } } });
      roots.forEach((r) => apply(r));
    });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    SOI.currency = {
      enabled: true,
      get: () => current,
      set: (c) => { desired = String(c).toUpperCase(); current = resolve(); apply(); },
      rates: () => ({ ...rates }),
      updatedAt: () => ratesAt,
      refresh: () => refreshRates().then((ok) => { current = resolve(); apply(); return ok; }),
    };
    // Fetch live rates on load when the cached ones are older than an hour, then keep them fresh while the tab stays open.
    const syncRates = () => {
      if (Date.now() - fetchedAt < RATES_TTL && Object.keys(rates).length > 1) return;
      refreshRates().then((ok) => { if (ok) { current = resolve(); apply(); } });
    };
    syncRates();
    setInterval(syncRates, 15 * 60 * 1000);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) syncRates(); });
    return { enabled: true, apply };
  })();

  /* ---------------- boot ---------------- */
  const boot = () => {
    if (Currency.enabled) Currency.apply();
    Wishlist.mark();
    Wishlist.renderPage();
    Wishlist.loadServer();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  void Cart; void Currency; void Search; void Filters; void SizeChart; void Complementary; void BackInStock;
})();
